/**
 * Sticky Header class
 * by default the header div needs to have the id #i96-stickyheader (can be set with the config variable 'selector_header')
 * for elementor the element also needs the .elementor-sticky--active class
 *
 * -hide-below:    hides after threshold pixel
 * -sticky-normal: css animation slide in / out
 * -direct-fade:   scrollrelated fade-in / -out
 *
 * v2.0 - 23.04.12
 * v2.1 - 23.10     renaming, fixing initial state
 *
 */

import m3_getQueryVariable from "../tools/m3-tools.js";

class I96StickyHeader {

  constructor(config) {

    this.debug = typeof m3_getQueryVariable == 'function' ? m3_getQueryVariable('debug-sticky-header') : false;

    const settings_default = {
      threshold: 200,
      selector_header: '#i96-stickyheader',
      selector_header_elementor: '#i96-stickyheader.elementor-sticky--active',
      wait_for_elementor: false,
      selector_placeholder_id: 'i96-stickyheader-placeholder'
    }

    this.settings = {
      ...settings_default,
      ...config
    }

    if (this.debug) console.log('I96StickyHeader: constructor('+config+')');

    this.variants = [
      '-hide-below',
      '-sticky-normal',
      '-direct-fade'
    ]

    this.izVisible = false

    this.scroll_data = {
      variant_1: {
        visible_height: 0,
        threshold: 0,
        visible: false,
        // pixel is quicker for smaller heights
        use_pixel: true
      },
      pos: {
        last: -1,
        curr: 0,
        diff: 1,
        /** Accumulated movement in scroll direction */
        dir_acc: 0,
        /** Direction of scroll movement */
        dir_change: true
      }
    }

    this.init_data = {
      wait_max: 100,
      wait_delay: 50,
      interval: null,
      wait_count: 0
    }

    this.init();

  }

  init() {
    // TODO: put the wait into an external class
    if (this.debug) console.log('I96StickyHeader: init()');

    if (document.querySelectorAll(this.settings.selector_header)) {

      if (this.debug) console.log('I96StickyHeader: found id');

      // waiting for initialization
      this.init_data.interval = setInterval(() => {

        // console.log(this.settings.selector_header_elementor, document.querySelectorAll(this.settings.selector_header_elementor), document.querySelectorAll(this.settings.selector_header_elementor).length);

        if (// header element found
            this._getHeaderElement().length
            // max wait cycles reached
            || (this.init_data.wait_count > this.init_data.wait_max)
        ) {

          clearInterval( this.init_data.interval )

          this._setup()

        } else {
          this.init_data.wait_count++
        }
      }, this.init_data.wait_delay)

    }

  }

  _setup() {

    this._setupPlaceholder()
    this._setupEvents()

    // setting initial visible height
    if ( this.$stickyheader ) {
      this.scroll_data.variant_1.visible_height = this.scroll_data.variant_1.use_pixel ? this.$stickyheader.getBoundingClientRect().height : 100
    }
  }

  /**
   * Returns the header element list, taking into account the value of the wait_for_elementor setting
   * @returns {NodeListOf<Element>}
   * @private
   */
  _getHeaderElement() {
    // elementor version
    if (this.settings.wait_for_elementor) {
      return document.querySelectorAll(this.settings.selector_header_elementor)
    // normal version
    } else {
      return document.querySelectorAll(this.settings.selector_header)
    }
  }

  /**
   * Returns the first header element found, otherwise false
   * @returns {Element|boolean}
   */
  getHeaderElement() {
    const el = this._getHeaderElement()
    if (el.length) {
      return el[0]
    }
    return false
  }

  _setupEvents() {

    if (this.debug) console.log('I96StickyHeader: _setupEvents()');

    if (!this.getHeaderElement()) {
      if (this.debug) console.log('I96StickyHeader: _setupEvents() > none found', this.settings.selector_header_elementor);
      return
    }

    this.$stickyheader = this.getHeaderElement();

    // console.log('stickyheader', this.$stickyheader);

    // get the variant
    for (const [key, val] of this.variants.entries()) {
      if (this.$stickyheader.classList.contains(val)) {
        this.variant = key
      }
    }

    // $(window).scroll(this._onScroll.bind(this))
    window.addEventListener(
        'scroll',
        this._onScroll.bind(this)
    );
    this._onScroll()

  }

  /**
   * Variant 1, hide the sticky header if it is below a given threshold
   * @private
   */
  _hideBelowThreshold() {
    if (this.debug) console.log('I96StickyHeader: _hideBelowThreshold()', this.settings.threshold, window.scrollY, this.scroll_data.pos.curr);

    if (this.scroll_data.pos.curr <= this.settings.threshold) {
      if (!this.izVisible) {
        this.$stickyheader.classList.add('-show-sticky');
        this.$stickyheader.classList.remove('-hide-sticky');
        this.izVisible = true
      }
    } else {
      if (this.izVisible) {
        this.$stickyheader.classList.remove('-show-sticky');
        this.$stickyheader.classList.add('-hide-sticky');
        this.izVisible = false
      }
    }
  }

  /**
   * Helper function to reset the animation state of the header to the initial values opacity: 1 and translateY: 0
   * @private
   */
  _resetHeaderFade() {
    this._setHeaderFade(1, '0');
  }

  /**
   * Helper function to set the animation state of the header
   * @param opacity
   * @param translatey
   * @private
   */
  _setHeaderFade(opacity, translatey) {
    this.$stickyheader.style.opacity = opacity;
    this.$stickyheader.style.transform = 'translateY('+translatey+')'
  }

  /**
   * Applies the 'fade state' depending on the current header position
   * @private
   */
  _directFade() {
    if (this.debug) console.log('I96StickyHeader: _directFade()', window.scrollY, this.scroll_data);

    if (this.$stickyheader == null) {
      return;
    }
    const max_visible_height = this.scroll_data.variant_1.use_pixel ? this.$stickyheader.getBoundingClientRect().height : 100


    // Don't fade the header when it is not stuck to the top of the screen (better to check it indirectly, no need to see the current fade state...)
    const trigger_rect = this.getTriggerRect()
    if ((trigger_rect.height + trigger_rect.top) > 0) {
      return;
    }

    // opacity
    this.scroll_data.variant_1.visible_height += this.scroll_data.pos.diff

    // TODO: don't do the calcs EVERY TIME

    // negative kinetic scroll fix
    if (window.scrollY >= 0) {
      this.$stickyheader.classList.remove('-negative-y');
    } else {
      this.$stickyheader.classList.add('-negative-y');
    }

    // hide if nothing is visible
    if (this.scroll_data.variant_1.visible_height < 0 && this.scroll_data.pos.curr > 0
      // fixing safari kinetic scroll problem where the header is hidden when we scroll up into negative space
      && window.scrollY > 0) {
      if (this.debug) console.log('I96StickyHeader: _directFade(): hide if nothing is visible');
      this.scroll_data.variant_1.visible = false
      this.scroll_data.variant_1.visible_height = 0

      this._setHeaderFade(0, '-100%');
      // this.$stickyheader.style.opacity = 0
      // this.$stickyheader.style.transform = 'translateY(-100%)'
    } else

    // show if fully visible
    if ( (this.scroll_data.variant_1.visible_height > max_visible_height) || this.scroll_data.pos.curr === 0) {
      if (this.debug) console.log('I96StickyHeader: _directFade(): show if fully visible', this.scroll_data.variant_1.visible_height, max_visible_height, this.scroll_data.pos.curr);
      this.scroll_data.variant_1.visible_height = max_visible_height
      this.scroll_data.variant_1.visible = true

      this._resetHeaderFade();
      // this.$stickyheader.style.opacity = 1
      // this.$stickyheader.style.transform = 'translateY(0)'

    // transition phase with < 1 opacity and position
    } else {
      // don't do it for safari negative y kinetic scroll
      if (window.scrollY > 0) {
        if (this.debug) console.log('I96StickyHeader: _directFade(): transition phase with < 1 opacity and position');

        this._setHeaderFade(
            (this.scroll_data.variant_1.visible_height / max_visible_height),
            '-' + (max_visible_height - this.scroll_data.variant_1.visible_height) +
            (this.scroll_data.variant_1.use_pixel ? 'px' : '%')
        )
        // this.$stickyheader.style.opacity = (this.scroll_data.variant_1.visible_height / max_visible_height)
        // this.$stickyheader.style.transform = 'translateY(-' + (max_visible_height - this.scroll_data.variant_1.visible_height) +
        //   (this.scroll_data.variant_1.use_pixel ? 'px' : '%') + ')';
      } else {
        console.log('window.scrollY smaller zero: ', window.scrollY);
      }
    }

  }


  // ----------------------------------------------------------------------
  //  PLACEHOLDER STUFF
  // ----------------------------------------------------------------------

  /**
   * Creates the placeholder element and inserts it right before the header element
   */
  _setupPlaceholder() {
    if (this.getHeaderElement() !== false) {
      var newNode = document.createElement('div');
      newNode.id = this.settings.selector_placeholder_id;
      newNode.classList.add('-hidden');
      this.getHeaderElement().parentNode.insertBefore(newNode, this.getHeaderElement());
    }

  }

  /**
   * Returns the placeholder element if found, false if not
   * @returns {HTMLElement|boolean}
   */
  getPlaceholderElement() {
    const placeholder = document.getElementById(this.settings.selector_placeholder_id);
    return placeholder ?? false
  }

  stickHeader() {
    if (this.getHeaderElement()) {
      this.getHeaderElement().classList.add('-stuck');
      this.showPlaceholder()
    }
  }
  unStickHeader() {
    if (this.getHeaderElement()) {
      this.getHeaderElement().classList.remove('-stuck');
      this.hidePlaceholder();
      this._resetHeaderFade();
    }
  }
  showPlaceholder() {
    if (this.getPlaceholderElement() !== false) {
      this.setPlaceholderHeight()
      this.getPlaceholderElement().classList.remove('-hidden')
    }
  }
  hidePlaceholder() {
    if (this.getPlaceholderElement() !== false) {
      this.getPlaceholderElement().classList.add('-hidden')
    }
  }

  /**
   * Sets the placeholder to the height of the header
   */
  setPlaceholderHeight() {
    if (this.getHeaderElement() !== false && this.getPlaceholderElement() !== false) {
      const header_height = this.getHeaderElement().getBoundingClientRect().height
      this.getPlaceholderElement().style.height = header_height + 'px'
    }
  }

  /**
   * Returns the trigger limit for the top or bottom, using the header or the placeholder (when the header is stuck)
   * @param top
   * @returns {number|number}
   */
  getTriggerRect() {
    return this.iz_stuck ? this.getPlaceholderElement().getBoundingClientRect() : this.$stickyheader.getBoundingClientRect()
  }

  checkStick() {
    if (this.debug) console.log('I96StickyHeader: checkStick(): ', this.getTriggerRect().top);

    const trigger_limit = this.getTriggerRect().top;

    if (trigger_limit < 0) {
      if (!this.iz_stuck) {
        if (this.debug) console.log('DO sticking');
        this.showPlaceholder()
        this.stickHeader()
        this.iz_stuck = true
      } else {
        // console.log('IZZ stuck');
      }
    } else {
      if (this.iz_stuck) {
        if (this.debug) console.log('UN sticking');
        this.unStickHeader()
        this.hidePlaceholder()
        this.iz_stuck = false
      } else {
        // console.log('IZZ UNstuck');
      }
    }
  }

  _onScroll() {

    if (this.debug) console.log('I96StickyHeader: _onScroll()', this.scroll_data);

    // calculate all the related values
    this.scroll_data.pos.curr = window.scrollY
    this.scroll_data.pos.diff = this.scroll_data.pos.last - this.scroll_data.pos.curr
    this.scroll_data.pos.dir_change =
           (this.scroll_data.pos.dir_acc < 0 && this.scroll_data.pos.diff > 0)
        || (this.scroll_data.pos.dir_acc > 0 && this.scroll_data.pos.diff < 0)
    if (this.scroll_data.pos.dir_change) {
      this.scroll_data.pos.dir_acc = 0
    }
    this.scroll_data.pos.dir_acc += this.scroll_data.pos.diff
    this.scroll_data.pos.dir_up = this.scroll_data.pos.dir_acc > 0
    this.scroll_data.pos.dir_down = !this.scroll_data.pos.dir_up

    // check if we have to stick the header
    this.checkStick()

    // VERSION 1: hide after threshold pixel
    switch (this.variant) {
      case 0:
        this._hideBelowThreshold()
        break;
      case 2:
        this._directFade()
        break;
    }

    this.scroll_data.pos.last = this.scroll_data.pos.curr

    if (this.debug) {
      let debugel = document.getElementById('i96-scroll-debug')
      if (!debugel) {
        var newNode = document.createElement('div');
        newNode.id = 'i96-scroll-debug';
        newNode.style.background = '#c0e3c0'
        newNode.style.position = 'fixed'
        newNode.style.top = '100px'
        newNode.style.left = '1rem'
        newNode.style.fontSize = '0.7rem'
        newNode.style.lineHeight = '1'
        newNode.style.padding = '0.5rem 1rem'
        document.body.appendChild(newNode);
        debugel = document.getElementById('i96-scroll-debug')
      }
      let str = ''
      // str += '<b style="min-width:64px;display:inline-block;">' + ele + '</b>' + this.scroll_data.pos[ele] + '<br>'
      Object.keys(this.scroll_data.pos).forEach( ele => {
        str += '<b style="min-width:64px;display:inline-block;">' + ele + '</b>' + this.scroll_data.pos[ele] + '<br>'
      })
      debugel.innerHTML = str
    }

  }

}

export default I96StickyHeader